"use client";

import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../config/axiosConfig";
import { formatMoney } from "../utils/utils";
import html2canvas from "html2canvas";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Necesario para que jsPDF (cargado via CDN en index.html) detecte html2canvas al usar .html()
window.html2canvas = html2canvas;

const parseFechaLocal = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
};

const formatearMes = (periodoStr) => {
    if (!periodoStr) return "";
    const [y, m] = periodoStr.split("-");
    const meses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
    return `${meses[parseInt(m, 10) - 1]} - ${y}`;
};

const formatearFechaPeriodo = (periodoStr) => {
    if (!periodoStr) return "";
    const [anioStr, mesStr] = periodoStr.split("-");
    const anio = parseInt(anioStr, 10);
    const mes = parseInt(mesStr, 10);
    if (!anio || !mes) return "";

    const meses = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
    const desde = new Date(anio, mes - 2, 26);
    const hasta = new Date(anio, mes - 1, 25);
    const incluyeAnio = desde.getFullYear() !== hasta.getFullYear();

    const format = (fecha) => {
        const mesLabel = meses[fecha.getMonth()];
        const anioLabel = incluyeAnio ? ` de ${fecha.getFullYear()}` : "";
        return `${fecha.getDate()} de ${mesLabel}${anioLabel}`;
    };

    return `${format(desde)} al ${format(hasta)}`;
};

const crearMensajeCobranzaDefault = (periodo, numeroNotificacion) => {
    const avisoPrefix = `Notificacion #${numeroNotificacion}`;
    return `<p>Estimado/a,</p><p>Se adjunta el detalle de cobranza correspondiente al periodo de ${formatearMes(periodo)} (${avisoPrefix}).</p><p>Quedamos atentos a cualquier consulta.</p><p>Saludos cordiales.</p>`;
};

// helper to format date
const formatearFecha = (fechaISO) => {
    if (!fechaISO) return "";
    const fecha = parseFechaLocal(fechaISO);
    const formatter = new Intl.DateTimeFormat("es-CL", { weekday: "long" });
    const dia = formatter.format(fecha);
    const [y, m, d] = fechaISO.split("-");
    const labelDia = dia.charAt(0).toUpperCase() + dia.slice(1);
    const diaCorto = labelDia.split(',')[0]; 
    return `${diaCorto} ${d}-${m}`;
};

const getPeriodoKey = (p) => {
    if (!p) return 'Día';
    const lower = p.toString().toLowerCase();
    return (lower === 'día' || lower === 'dia' || lower === '1') ? 'Día' : 'Noche';
};

const tieneAuxiliarAsignado = (item) => {
    const auxiliarId = Number(item?.auxiliar_id) || 0;
    const auxiliarNombre = (item?.auxiliar_nombre || "").trim();
    return auxiliarId > 0 || auxiliarNombre !== "";
};

const pickFirstArray = (...values) => values.find(Array.isArray) || [];

const pickFirstObject = (...values) =>
    values.find(value => value && typeof value === "object" && !Array.isArray(value)) || null;

const pickFirstDefined = (...values) =>
    values.find(value => value !== undefined && value !== null);

const normalizarDetalleCobranza = (payload) => {
    if (!payload || typeof payload !== "object") return null;

    const snapshot =
        pickFirstObject(
            payload.snapshot,
            payload.detalle_snapshot,
            payload.detalle_liquidado,
            payload.snapshot_detalle,
            payload.detalle_historico,
            payload.historico
        ) || {};

    const detalle = pickFirstObject(payload.detalle, snapshot.detalle) || {};

    const turnos = pickFirstArray(
        payload.turnos,
        detalle.turnos,
        snapshot.turnos
    );

    const adicionales = pickFirstArray(
        payload.adicionales,
        detalle.adicionales,
        snapshot.adicionales
    );

    return {
        ...payload,
        ...snapshot,
        ...detalle,
        paciente: pickFirstObject(payload.paciente, detalle.paciente, snapshot.paciente),
        tutor: pickFirstObject(payload.tutor, detalle.tutor, snapshot.tutor),
        turnos,
        adicionales,
        saldo_anterior: Number(pickFirstDefined(payload.saldo_anterior, detalle.saldo_anterior, snapshot.saldo_anterior, 0)) || 0,
        otros_suma: Number(pickFirstDefined(payload.otros_suma, detalle.otros_suma, snapshot.otros_suma, 0)) || 0,
        otros_resta: Number(pickFirstDefined(payload.otros_resta, detalle.otros_resta, snapshot.otros_resta, 0)) || 0,
        observacion: pickFirstDefined(payload.observacion, detalle.observacion, snapshot.observacion, ""),
        is_snapshot: Boolean(
            payload.is_snapshot ||
            detalle.is_snapshot ||
            snapshot.is_snapshot ||
            payload.liquidado ||
            detalle.liquidado ||
            snapshot.liquidado ||
            turnos.some(item => item?.is_snapshot) ||
            adicionales.some(item => item?.is_snapshot)
        ),
    };
};

const clonarDetalleEditable = (detalle) => ({
    ...detalle,
    turnos: (detalle?.turnos || []).map(item => ({ ...item })),
    adicionales: (detalle?.adicionales || []).map(item => ({ ...item })),
    otros_suma: Number(detalle?.otros_suma || 0),
    otros_resta: Number(detalle?.otros_resta || 0),
    observacion: detalle?.observacion || "",
});

const nombreAuxiliarAdicional = (item) =>
    item?.auxiliar_reemplazo_nombre ||
    item?.auxiliar_original_nombre ||
    item?.auxiliar_nombre ||
    "";

function EditarCobranzaModal({ visible, onClose, detalleInicial, pacienteID, pacienteNombre, periodo, onSaved }) {
    const [loading, setLoading] = useState(false);
    const [motivoLog, setMotivoLog] = useState("");
    const [editData, setEditData] = useState(() => clonarDetalleEditable(detalleInicial));

    useEffect(() => {
        if (visible) {
            setEditData(clonarDetalleEditable(detalleInicial));
            setMotivoLog("");
        }
    }, [visible, detalleInicial]);

    if (!visible) return null;

    // ---------- helpers de update ----------
    const updateTurno = (index, field, value) => {
        setEditData(prev => {
            const next = clonarDetalleEditable(prev);
            next.turnos[index] = {
                ...next.turnos[index],
                [field]: field === "fecha" || field === "periodo" ? value : (Number(value) || 0)
            };
            return next;
        });
    };

    const updateAdicional = (index, field, value) => {
        setEditData(prev => {
            const next = clonarDetalleEditable(prev);
            next.adicionales[index] = {
                ...next.adicionales[index],
                [field]: ["fecha","fechaAdicional","periodo","tipo_fecha","motivo"].includes(field)
                    ? value : (Number(value) || 0)
            };
            return next;
        });
    };

    // ---------- construir matriz igual al detalle ----------
    const buildEditMatrix = () => {
        const fechasSet = new Set();
        const auxiliaresSet = new Set();

        (editData.turnos || []).forEach(t => {
            const f = (t.fecha || "").split(" ")[0];
            if (f) fechasSet.add(f);
            if (t.auxiliar_nombre) auxiliaresSet.add(t.auxiliar_nombre);
        });
        (editData.adicionales || []).forEach(a => {
            const f = ((a.fecha || a.fechaAdicional) || "").split(" ")[0];
            if (f) fechasSet.add(f);
            let nombre = nombreAuxiliarAdicional(a);

            if (!nombre && f) {
                const p = getPeriodoKey(a.periodo || a.tipo_fecha);
                const turnoAsoc = (editData.turnos || []).find(t => 
                    (t.fecha || "").split(" ")[0] === f && getPeriodoKey(t.periodo) === p
                );
                if (turnoAsoc) nombre = turnoAsoc.auxiliar_nombre;
                else nombre = (auxiliaresSet.size > 0 ? [...auxiliaresSet][0] : "General"); 
            }

            if (nombre) auxiliaresSet.add(nombre);
        });

        const fechas = [...fechasSet].sort();
        const auxiliares = [...auxiliaresSet].sort();

        // cell tracks FIRST matching index from each array (typical: one turno per fecha/periodo/aux)
        const makeCell = () => ({ turnoIdx: null, adicionalIdxs: [] });

        const matrix = {};
        fechas.forEach(f => {
            matrix[f] = {
                "Día":   Object.fromEntries(auxiliares.map(a => [a, makeCell()])),
                "Noche": Object.fromEntries(auxiliares.map(a => [a, makeCell()])),
            };
        });

        (editData.turnos || []).forEach((t, idx) => {
            const f = (t.fecha || "").split(" ")[0];
            const p = getPeriodoKey(t.periodo);
            const a = t.auxiliar_nombre || "";
            if (matrix[f]?.[p]?.[a] !== undefined && matrix[f][p][a].turnoIdx === null) {
                matrix[f][p][a].turnoIdx = idx;
            }
        });

        (editData.adicionales || []).forEach((ad, idx) => {
            const f = ((ad.fecha || ad.fechaAdicional) || "").split(" ")[0];
            const p = getPeriodoKey(ad.periodo || ad.tipo_fecha);
            let a = nombreAuxiliarAdicional(ad);

            // Fallback: si no tiene nombre, intentar asociar al turno de ese día/periodo
            if (!a && f && p) {
                const turnoAsoc = (editData.turnos || []).find(t => 
                    (t.fecha || "").split(" ")[0] === f && getPeriodoKey(t.periodo) === p
                );
                if (turnoAsoc) a = turnoAsoc.auxiliar_nombre;
                else a = auxiliares[0] || "General";
            }

            if (matrix[f]?.[p]?.[a] !== undefined) {
                matrix[f][p][a].adicionalIdxs.push(idx);
            }
        });

        return { fechas, auxiliares, matrix };
    };

    const { fechas, auxiliares, matrix } = buildEditMatrix();
    const resumenEditado = calcularResumenDetalleCobranza(editData);
    const totalPeriodoEditado = resumenEditado.totalGeneral;

    // ---------- render celda editable ----------
    const renderCeldaEditable = (cell) => {
        const { turnoIdx, adicionalIdxs } = cell;
        const t   = turnoIdx !== null ? editData.turnos[turnoIdx] : null;
        const ads = adicionalIdxs.map(i => editData.adicionales[i]);

        if (!t && ads.length === 0) return <span className="text-muted">-</span>;

        const inp = (val, onChange, label, colorClass = "text-muted") => (
            <div className="d-flex align-items-center gap-1 mb-1">
                <small className={`${colorClass} text-nowrap`} style={{ width: "36px", fontSize: "0.7rem" }}>{label}</small>
                <input
                    type="number"
                    className="form-control form-control-sm p-0 px-1 text-end"
                    style={{ width: "78px", height: "22px", fontSize: "0.78rem" }}
                    value={val}
                    onChange={e => onChange(e.target.value)}
                    disabled={loading}
                />
            </div>
        );

        return (
            <div style={{ minWidth: "130px" }}>
                {t && (
                    <>
                        {inp(Number(t.valor_turno_paciente || 0), v => updateTurno(turnoIdx, "valor_turno_paciente", v), "Turno:")}
                        {inp(Number(t.colacion_paciente    || 0), v => updateTurno(turnoIdx, "colacion_paciente",    v), "Alim.:")}
                        {inp(Number(t.locomocion_paciente  || 0), v => updateTurno(turnoIdx, "locomocion_paciente",  v), "Mov.:")}
                        {Number(t.otro_paciente || 0) > 0 &&
                            inp(Number(t.otro_paciente || 0), v => updateTurno(turnoIdx, "otro_paciente", v), "Otro:")}
                    </>
                )}
                {ads.map((ad, i) => {
                    const adIdx = adicionalIdxs[i];
                    return (
                        <div key={adIdx} className="mt-1 pt-1" style={{ borderTop: (i === 0 && t) ? "1px dashed #ccc" : "none" }}>
                            {/* Campos principales del adicional */}
                            {inp(Number(ad.valor_turno_paciente  || 0), v => updateAdicional(adIdx, "valor_turno_paciente",  v), "Adic.:",  "text-primary")}
                            {inp(Number(ad.horas_extras_paciente || 0), v => updateAdicional(adIdx, "horas_extras_paciente", v), "HE:",     "text-warning")}
                            {inp(Number(ad.colacion_paciente     || 0), v => updateAdicional(adIdx, "colacion_paciente",     v), "Alim.:")}
                            {inp(Number(ad.locomocion_paciente   || 0), v => updateAdicional(adIdx, "locomocion_paciente",   v), "Mov.:")}
                            
                            {/* Fuera de Santiago (F. Stgo) */}
                            {Number(ad.recargo_fs_turno || 0) > 0 && 
                                inp(Number(ad.recargo_fs_turno || 0), v => updateAdicional(adIdx, "recargo_fs_turno", v), "F.Stgo T:", "text-success")}
                            {Number(ad.recargo_fs_colacion || 0) > 0 && 
                                inp(Number(ad.recargo_fs_colacion || 0), v => updateAdicional(adIdx, "recargo_fs_colacion", v), "F.Stgo A:", "text-success")}

                            {/* Otros */}
                            {Number(ad.otro_paciente || 0) > 0 && 
                                inp(Number(ad.otro_paciente || 0), v => updateAdicional(adIdx, "otro_paciente", v), "Otro:")}
                            
                            {ad.motivo && (
                                <div className="text-muted" style={{ fontSize: "0.68rem", fontStyle: "italic" }}>({ad.motivo})</div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // ---------- guardar ----------
    const guardarCambios = async () => {
        if (!motivoLog.trim()) {
            alert("Debe indicar el motivo del ajuste para registrar la trazabilidad.");
            return;
        }
        try {
            setLoading(true);
            const response = await axiosInstance.post("/acuerdo-mutuo/cobranza/detalle/editar", {
                paciente_id: pacienteID,
                periodo,
                motivo_log: motivoLog.trim(),
                actualizar_cabecera: 1,
                actualizar_cartola: 1,
                otros_suma:  Number(editData?.otros_suma  || 0),
                otros_resta: Number(editData?.otros_resta || 0),
                observacion: editData?.observacion || "",
                turnos:      editData?.turnos      || [],
                adicionales: editData?.adicionales || [],
            });
            if (response.data?.error) throw new Error(response.data.message || "No se pudo actualizar la cobranza.");
            alert(response.data?.message || "Cobranza actualizada correctamente.");
            if (onSaved) onSaved();
            onClose();
        } catch (error) {
            console.error(error);
            alert(error?.message || "Error al guardar cambios de cobranza.");
        } finally {
            setLoading(false);
        }
    };

    // ---------- render ----------
    return (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1065 }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content border-0 shadow-lg">

                    {/* HEADER */}
                    <div className="modal-header bg-warning">
                        <div>
                            <h5 className="modal-title text-dark mb-0">
                                <i className="bi bi-pencil-square me-2"></i>Editar Cobranza Liquidada
                            </h5>
                            <small className="text-dark opacity-75">{pacienteNombre} — {formatearMes(periodo)}</small>
                        </div>
                        <button className="btn-close" onClick={onClose} disabled={loading}></button>
                    </div>

                    <div className="modal-body">
                        {/* Alerta */}
                        <div className="alert alert-warning py-2 mb-3">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            Esta edición recalculará la cabecera y el cobro en cartola. El motivo es obligatorio.
                        </div>

                        {/* Controles superiores: ajustes + motivo */}
                        <div className="p-3 mb-4 border rounded bg-light">
                            <div className="row g-3 align-items-end">
                                <div className="col-md-3">
                                    <label className="form-label fw-bold small">Otros Suma (+)</label>
                                    <div className="input-group input-group-sm">
                                        <span className="input-group-text">$</span>
                                        <input type="number" className="form-control"
                                            value={editData.otros_suma}
                                            onChange={e => setEditData(p => ({ ...p, otros_suma: Number(e.target.value) || 0 }))}
                                            disabled={loading} />
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold small">Otros Resta (–)</label>
                                    <div className="input-group input-group-sm">
                                        <span className="input-group-text">$</span>
                                        <input type="number" className="form-control"
                                            value={editData.otros_resta}
                                            onChange={e => setEditData(p => ({ ...p, otros_resta: Number(e.target.value) || 0 }))}
                                            disabled={loading} />
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold small">Total Estimado</label>
                                    <div className="form-control form-control-sm fw-bold text-success bg-white text-end">
                                        ${formatMoney(totalPeriodoEditado)}
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold small">Observación</label>
                                    <input type="text" className="form-control form-control-sm"
                                        value={editData.observacion || ""}
                                        onChange={e => setEditData(p => ({ ...p, observacion: e.target.value }))}
                                        disabled={loading} />
                                </div>
                                <div className="col-12">
                                    <label className="form-label fw-bold small text-danger">* Motivo del Ajuste (requerido)</label>
                                    <textarea className="form-control form-control-sm border-danger" rows="2"
                                        value={motivoLog}
                                        onChange={e => setMotivoLog(e.target.value)}
                                        disabled={loading}
                                        placeholder="Explique por qué se modifica esta cobranza liquidada." />
                                </div>
                            </div>
                        </div>

                        {/* ---- MATRIZ IGUAL AL DETALLE ---- */}
                        {fechas.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-bordered table-striped" style={{ fontSize: "0.85rem" }}>
                                    <thead className="table-dark align-middle text-center">
                                        <tr>
                                            <th style={{ width: "80px" }}>Fecha</th>
                                            <th style={{ width: "60px" }}>Turno</th>
                                            {auxiliares.map(a => (
                                                <th key={a} style={{ minWidth: "150px" }}>{a}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fechas.map(f => {
                                            const rowDia   = matrix[f]["Día"];
                                            const rowNoche = matrix[f]["Noche"];
                                            const tieneAlgo = auxiliares.some(a =>
                                                rowDia[a].turnoIdx !== null   || rowDia[a].adicionalIdxs.length > 0 ||
                                                rowNoche[a].turnoIdx !== null || rowNoche[a].adicionalIdxs.length > 0
                                            );
                                            if (!tieneAlgo) return null;
                                            return (
                                                <React.Fragment key={f}>
                                                    {/* FILA DÍA */}
                                                    <tr>
                                                        <td rowSpan={2} className="align-middle fw-bold text-center">
                                                            {formatearFecha(f)}
                                                        </td>
                                                        <td className="text-danger fw-bold text-center align-middle">Día</td>
                                                        {auxiliares.map(a => (
                                                            <td key={a} className="align-top p-2">
                                                                {renderCeldaEditable(rowDia[a])}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                    {/* FILA NOCHE */}
                                                    <tr>
                                                        <td className="text-primary fw-bold text-center align-middle">Noche</td>
                                                        {auxiliares.map(a => (
                                                            <td key={a} className="align-top p-2">
                                                                {renderCeldaEditable(rowNoche[a])}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="alert alert-info text-center">No hay turnos registrados para editar.</div>
                        )}
                    </div>

                    {/* FOOTER */}
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancelar</button>
                        <button className="btn btn-warning fw-bold" onClick={guardarCambios} disabled={loading}>
                            {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Guardando...</> : "Guardar y Recalcular"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

const calcularResumenDetalleCobranza = (data) => {
    const turnosValidos = (data?.turnos || []).filter(tieneAuxiliarAsignado);
    const adicionales = data?.adicionales || [];

    const totalTurnosBaseFS = turnosValidos.reduce(
        (sum, turno) => sum + (Number(turno.valor_turno_paciente) || 0),
        0
    ) + adicionales.reduce(
        (sum, adicional) => sum + (Number(adicional.valor_turno_paciente) || 0) + (Number(adicional.recargo_fs_turno) || 0),
        0
    );

    const totalBeneficios = turnosValidos.reduce(
        (sum, turno) => sum + (Number(turno.colacion_paciente) || 0) + (Number(turno.locomocion_paciente) || 0),
        0
    ) + adicionales.reduce(
        (sum, adicional) =>
            sum +
            (Number(adicional.colacion_paciente) || 0) +
            (Number(adicional.recargo_fs_colacion) || 0) +
            (Number(adicional.locomocion_paciente) || 0) +
            (Number(adicional.recargo_fs_locomocion) || 0),
        0
    );

    const totalOtrosHE = turnosValidos.reduce(
        (sum, turno) => sum + (Number(turno.otro_paciente) || 0),
        0
    ) + adicionales.reduce(
        (sum, adicional) =>
            sum +
            (Number(adicional.otro_paciente) || 0) +
            (Number(adicional.recargo_fs_otro) || 0) +
            (Number(adicional.horas_extras_paciente) || 0),
        0
    );

    const totalGeneral = turnosValidos.reduce(
        (sum, turno) => sum + (Number(turno.subtotal) || 0),
        0
    ) + adicionales.reduce(
        (sum, adicional) => sum + (Number(adicional.subtotal) || 0),
        0
    );

    return {
        totalTurnosBaseFS,
        totalBeneficios,
        totalOtrosHE,
        totalGeneral,
    };
};

const FIRMA_DEFAULT = `
    <table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif; font-size: 13px; color:#333; line-height: 1.35;">
      <tr>
        <!-- Datos -->
        <td style="padding: 0 18px 0 0; vertical-align: top; white-space: nowrap;">
          <div style="font-size: 14px; font-weight: 700; color:#2b2b2b;">M. Angélica Palma P.</div>
          <div style="color:#666;">Directora Administrativa</div>

          <div style="margin-top: 6px; font-weight: 700; letter-spacing: .2px;">SK MEDICAL CARE</div>

          <div style="margin-top: 6px;">
            <span style="color:#666;">Móvil:</span>
            <a href="tel:+5692305037" style="color:#0b74b8; text-decoration: none;">+56 9 2305 037</a>
          </div>

          <div style="margin-top: 4px;">
            <span style="color:#666;">Dirección:</span> El Trovador 4280 Of 403 LC
          </div>

          <div style="margin-top: 6px;">
            <a href="https://skmc.cl" style="color:#0b74b8; text-decoration: none;">www.skmc.cl</a>
          </div>
        </td>

        <!-- Logo -->
        <td style="vertical-align: middle; padding: 0; border-left: 1px solid #e6e6e6; padding-left: 18px;">
          <a href="https://skmc.cl" style="text-decoration:none;">
            <img
              src="https://skmc.cl/wp-content/uploads/2024/10/sk-logo.png"
              alt="SK Medical Care"
              style="display:block; border:0; outline:none; text-decoration:none; height:90px; width:auto;"
            />
          </a>
        </td>
      </tr>
    </table>
`;

const CobranzaDetalle = ({ visible, onClose, periodo, isLiquidado = false, pacienteID, pacienteNombre, modoNotificacion, onNotificacionEnviada, pacienteData }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [showEditarModal, setShowEditarModal] = useState(false);

    // Estados para notificar
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [notificationType, setNotificationType] = useState("1");
    const [emailMessage, setEmailMessage] = useState("");
    const [emailDestinatarios, setEmailDestinatarios] = useState("");
    const [emailAsunto, setEmailAsunto] = useState("");
    const [emailAdjuntosSelect, setEmailAdjuntosSelect] = useState(null);
    const [fileBase64, setFileBase64] = useState(null);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState("");

    const resumenDetalle = calcularResumenDetalleCobranza(data);
    const totalGeneralCalculado = resumenDetalle.totalGeneral;
    const saldoAnterior = Number(data?.saldo_anterior) || 0;
    const consolidado = saldoAnterior + totalGeneralCalculado;

    const cargarEmailTemplates = useCallback(async () => {
        try {
            const resp = await axiosInstance.get("/administracion/correo-templates", {
                params: { activos: 1 }
            });
            setEmailTemplates(resp.data.data || []);
        } catch (error) {
            console.error("Error al cargar templates de correo:", error);
            setEmailTemplates([]);
        }
    }, []);

    const getValorTemplate = useCallback((campo) => {
        const fuentes = [pacienteData || {}, data?.paciente || {}, data?.tutor || {}];
        for (const fuente of fuentes) {
            if (fuente && fuente[campo]) return fuente[campo];
        }
        return "";
    }, [pacienteData, data]);

    const renderTemplateCorreo = useCallback((cuerpo) => {
        const variables = {
            nombre_tutor: data?.tutor?.nombre || getValorTemplate("tutor_nombre") || getValorTemplate("nombre_tutor") || "",
            nombre_paciente: pacienteNombre || getValorTemplate("nombre_paciente") || getValorTemplate("paciente_nombre") || "",
            periodo: formatearMes(periodo),
            fecha_periodo: formatearFechaPeriodo(periodo),
        };

        return (cuerpo || "")
            .replace(/\{nombre_tutor\}/g, variables.nombre_tutor)
            .replace(/\{nombre_paciente\}/g, variables.nombre_paciente)
            .replace(/\{periodo\}/g, variables.periodo)
            .replace(/\{fecha_periodo\}/g, variables.fecha_periodo);
    }, [data, getValorTemplate, pacienteNombre, periodo]);

    const handleSeleccionTemplate = (templateId) => {
        setSelectedTemplateId(templateId);

        if (!templateId) {
            setEmailMessage(crearMensajeCobranzaDefault(periodo, notificationType));
            return;
        }

        const template = emailTemplates.find((item) => String(item.id) === String(templateId));
        if (template) {
            setEmailMessage(renderTemplateCorreo(template.cuerpo));
        }
    };

    const handleCopiarImagen = async () => {
        const element = document.getElementById("detalle-cobranza-contenido");
        if (!element) return;
        try {
            const canvas = await html2canvas(element, { scale: 1, backgroundColor: "#ffffff" });
            canvas.toBlob((blob) => {
                if (blob) {
                    navigator.clipboard.write([new ClipboardItem({ "image/png": blob })])
                        .then(() => alert("Imagen copiada al portapapeles."))
                        .catch(err => console.error("Error al copiar:", err));
                }
            });
        } catch (error) {
            console.error("Error imagen:", error);
        }
    };

    const handleDescargarPDF = async () => {
        const element = document.getElementById("detalle-cobranza-contenido");
        if (!element) return;
        try {
            setLoading(true);
            const canvas = await html2canvas(element, { scale: 1.5, useCORS: true, backgroundColor: "#ffffff" });
            const base64image = canvas.toDataURL("image/jpeg", 0.7);
            
            // Crear PDF a partir del canvas con múltiples páginas y márgenes
            const doc = new window.jspdf.jsPDF('p', 'mm', 'a4', true); // compress: true
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const marginX = 10;
            const marginY = 10;
            const pdfWidth = pageWidth - (marginX * 2);
            const pdfContentHeight = pageHeight - (marginY * 2);
            
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            let heightLeft = pdfHeight;
            let position = marginY;

            doc.addImage(base64image, 'JPEG', marginX, position, pdfWidth, pdfHeight, undefined, 'FAST');
            heightLeft -= pdfContentHeight;
            
            // Ocultar desbordamiento en la primera página
            doc.setFillColor(255, 255, 255);
            doc.rect(0, pageHeight - marginY, pageWidth, marginY, 'F');

            while (heightLeft > 0) {
                position -= pdfContentHeight;
                doc.addPage();
                doc.addImage(base64image, 'JPEG', marginX, position, pdfWidth, pdfHeight, undefined, 'FAST');
                
                // Ocultar margen superior
                doc.setFillColor(255, 255, 255);
                doc.rect(0, 0, pageWidth, marginY, 'F');
                // Ocultar margen inferior
                doc.rect(0, pageHeight - marginY, pageWidth, marginY, 'F');
                
                heightLeft -= pdfContentHeight;
            }

            doc.save(`Detalle_Cobranza_${pacienteNombre.replace(/\s+/g, '_')}_${periodo}.pdf`);
        } catch (error) {
            console.error("Error al generar PDF:", error);
            alert("No se pudo generar el PDF");
        } finally {
            setLoading(false);
        }
    };

    const handleDescargarPDF_HTML = async () => {
        if (!data) return;
        try {
            setLoading(true);
            const doc = new window.jspdf.jsPDF('p', 'mm', 'a4');
            
            // Título y Encabezados info
            doc.setFontSize(16);
            doc.setTextColor(40);
            doc.text(`Detalle de Cobranza - ${pacienteNombre}`, 14, 15);
            
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Periodo: ${formatearMes(periodo)}`, 14, 22);
            doc.text(`Paciente: ${pacienteNombre}`, 14, 27);
            
            // --- NUEVA SECCIÓN: TOTALES DEL PERIODO ---
            doc.setDrawColor(200);
            doc.line(14, 30, 196, 30);
            
            doc.setFontSize(9);
            doc.setTextColor(0);
            
            const { totalTurnosBaseFS, totalBeneficios, totalOtrosHE, totalGeneral } = resumenDetalle;

            doc.setFont(undefined, 'bold');
            doc.text("Total Turnos (Base + FS):", 14, 36);
            doc.setFont(undefined, 'normal');
            doc.text(`$${formatMoney(totalTurnosBaseFS)}`, 60, 36);

            doc.setFont(undefined, 'bold');
            doc.text("Beneficios (Alim. + Mov.):", 14, 41);
            doc.setFont(undefined, 'normal');
            doc.text(`$${formatMoney(totalBeneficios)}`, 60, 41);

            doc.setFont(undefined, 'bold');
            doc.text("Otros / Horas Extras:", 110, 36);
            doc.setFont(undefined, 'normal');
            doc.text(`$${formatMoney(totalOtrosHE)}`, 150, 36);

            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            
            // We just format TOTAL DEL MES at the top as it originally was
            doc.setTextColor(0, 128, 0); // Verde
            doc.text("Total del Mes:", 110, 43);
            doc.text(`$${formatMoney(totalGeneral)}`, 155, 43);
            
            doc.setTextColor(0);
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            // -------------------------------------------

            // Preparar Datos para la Tabla
            const tableHeaders = ["Fecha", "Turno", ...auxiliares, "Total Día"];
            const tableRows = [];

            fechas.forEach(f => {
                ['Día', 'Noche'].forEach(pKey => {
                    const row = new Array(tableHeaders.length).fill("-");
                    row[0] = f;
                    row[1] = pKey;
                    
                    let rowTotal = 0;
                    let hasData = false;

                    auxiliares.forEach(aux => {
                        const cell = matrix[f][pKey][aux] || { turno: 0, colacion: 0, movilizacion: 0, he: 0, adicional: 0, esFS: false };
                        
                        const tVal = (Number(cell.turno) || 0);
                        const aVal = (Number(cell.colacion) || 0);
                        const mVal = (Number(cell.movilizacion) || 0);
                        const hVal = (Number(cell.he) || 0);
                        const oVal = (Number(cell.adicional) || 0);
                        const totalCell = tVal + aVal + mVal + hVal + oVal;

                        if (totalCell > 0) {
                            let detail = [];
                            if (tVal > 0) detail.push(`Turno: $${formatMoney(tVal)}`);
                            if (cell.esFS) detail.push(`(F.SANTIAGO)`);
                            if (aVal > 0) detail.push(`Alim: $${formatMoney(aVal)}`);
                            if (mVal > 0) detail.push(`Mov: $${formatMoney(mVal)}`);
                            if (hVal > 0) detail.push(`HE: $${formatMoney(hVal)}`);
                            if (oVal > 0) detail.push(`Otros: $${formatMoney(oVal)}`);
                            
                            row[tableHeaders.indexOf(aux)] = detail.join("\n");
                            rowTotal += totalCell;
                            hasData = true;
                        }
                    });

                    if (hasData) {
                        row[tableHeaders.length - 1] = `$${formatMoney(rowTotal)}`;
                        tableRows.push(row);
                    }
                });
            });

            // Fila de totales de la tabla
            const totalCols = new Array(tableHeaders.length).fill("");
            totalCols[0] = "TOTALES";
            let grandTotalTable = 0;
            auxiliares.forEach(a => {
                const totalAux = fechas.reduce((sum, f) => {
                    const d = matrix[f]['Día'][a] || {};
                    const n = matrix[f]['Noche'][a] || {};
                    const dSum = (Number(d.turno)||0) + (Number(d.colacion)||0) + (Number(d.movilizacion)||0) + (Number(d.he)||0) + (Number(d.adicional)||0);
                    const nSum = (Number(n.turno)||0) + (Number(n.colacion)||0) + (Number(n.movilizacion)||0) + (Number(n.he)||0) + (Number(n.adicional)||0);
                    return sum + dSum + nSum;
                }, 0);
                totalCols[tableHeaders.indexOf(a)] = `$${formatMoney(totalAux)}`;
                grandTotalTable += totalAux;
            });
            totalCols[tableHeaders.length - 1] = `$${formatMoney(grandTotalTable)}`;

            doc.autoTable({
                head: [tableHeaders],
                body: tableRows,
                startY: 50,
                theme: 'grid',
                styles: { fontSize: 7, cellPadding: 1.5 },
                headStyles: { fillColor: [43, 43, 43], textColor: 255 },
                columnStyles: {
                    0: { cellWidth: 20 },
                    1: { cellWidth: 15 }
                },
                foot: [totalCols],
                footStyles: { fillColor: [220, 220, 220], textColor: 0, fontStyle: 'bold' },
                showFoot: 'lastPage'
            });

            // NUEVO: Agregar totales debajo de la tabla al final
            const saldoAnt = saldoAnterior;
            let finalY = doc.lastAutoTable.finalY + 10;

            if (saldoAnt > 0) {
                if (finalY > doc.internal.pageSize.getHeight() - 30) {
                    doc.addPage();
                    finalY = 20;
                }
                doc.setFontSize(10);
                doc.setFont(undefined, 'bold');
                
                doc.setTextColor(0);
                doc.text("Total del Mes:", 110, finalY);
                doc.text(`$${formatMoney(totalGeneral)}`, 155, finalY);
                
                finalY += 5;
                doc.setTextColor(200, 0, 0); // Rojo
                doc.text("Deuda Anterior:", 110, finalY);
                doc.text(`$${formatMoney(saldoAnt)}`, 155, finalY);
                
                finalY += 5;
                doc.setTextColor(0, 128, 0); // Verde
                doc.text("Total Final:", 110, finalY);
                doc.text(`$${formatMoney(totalGeneral + saldoAnt)}`, 155, finalY);
            }

            doc.save(`Detalle_Cobranza_LIGERO_${pacienteNombre.replace(/\s+/g, '_')}_${periodo}.pdf`);
        } catch (error) {
            console.error("Error al generar PDF Ligero:", error);
            alert("No se pudo generar el PDF ligero.");
        } finally {
            setLoading(false);
        }
    };

    const handleEnviarCorreo = async () => {
        const element = document.getElementById("detalle-cobranza-contenido");
        if (!element || !data) {
            alert("No se pudo cargar el detalle para enviarlo");
            return;
        }
        try {
            setLoading(true);
            
            // 1. Capturamos la imagen para el cuerpo del correo (CID) - Calidad baja para velocidad
            const canvas = await html2canvas(element, { scale: 1.2, useCORS: true, backgroundColor: "#ffffff" });
            const base64image = canvas.toDataURL("image/jpeg", 0.6);

            // 2. Generamos el PDF LIGERO para adjuntar (usando la misma lógica de handleDescargarPDF_HTML)
            const doc = new window.jspdf.jsPDF('p', 'mm', 'a4');
            doc.setFontSize(16);
            doc.setTextColor(40);
            doc.text(`Detalle de Cobranza - ${pacienteNombre}`, 14, 15);
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Periodo: ${formatearMes(periodo)}`, 14, 22);
            doc.text(`Paciente: ${pacienteNombre}`, 14, 27);
            doc.setDrawColor(200);
            doc.line(14, 30, 196, 30);
            
            const { totalTurnosBaseFS, totalBeneficios, totalOtrosHE, totalGeneral } = resumenDetalle;

            doc.setFontSize(9);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text("Total Turnos (Base + FS):", 14, 36);
            doc.setFont(undefined, 'normal');
            doc.text(`$${formatMoney(totalTurnosBaseFS)}`, 60, 36);
            doc.setFont(undefined, 'bold');
            doc.text("Beneficios (Alim. + Mov.):", 14, 41);
            doc.setFont(undefined, 'normal');
            doc.text(`$${formatMoney(totalBeneficios)}`, 60, 41);
            doc.setFont(undefined, 'bold');
            doc.text("Otros / Horas Extras:", 110, 36);
            doc.setFont(undefined, 'normal');
            doc.text(`$${formatMoney(totalOtrosHE)}`, 150, 36);
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            
            doc.setTextColor(0, 128, 0);
            doc.text("Total del Mes:", 110, 43);
            doc.text(`$${formatMoney(totalGeneral)}`, 155, 43);

            const tableHeaders = ["Fecha", "Turno", ...auxiliares, "Total Día"];
            const tableRows = [];
            fechas.forEach(f => {
                ['Día', 'Noche'].forEach(pKey => {
                    const row = new Array(tableHeaders.length).fill("-");
                    row[0] = f;
                    row[1] = pKey;
                    let rowTotal = 0;
                    let hasData = false;
                    auxiliares.forEach(aux => {
                        const cell = matrix[f][pKey][aux] || { turno: 0, colacion: 0, movilizacion: 0, he: 0, adicional: 0, esFS: false };
                        const tVal = (Number(cell.turno) || 0);
                        const aVal = (Number(cell.colacion) || 0);
                        const mVal = (Number(cell.movilizacion) || 0);
                        const hVal = (Number(cell.he) || 0);
                        const oVal = (Number(cell.adicional) || 0);
                        const totalCell = tVal + aVal + mVal + hVal + oVal;
                        if (totalCell > 0) {
                            let detail = [];
                            if (tVal > 0) detail.push(`Turno: $${formatMoney(tVal)}`);
                            if (cell.esFS) detail.push(`(F.SANTIAGO)`);
                            if (aVal > 0) detail.push(`Alim: $${formatMoney(aVal)}`);
                            if (mVal > 0) detail.push(`Mov: $${formatMoney(mVal)}`);
                            if (hVal > 0) detail.push(`HE: $${formatMoney(hVal)}`);
                            if (oVal > 0) detail.push(`Otros: $${formatMoney(oVal)}`);
                            row[tableHeaders.indexOf(aux)] = detail.join("\n");
                            rowTotal += totalCell;
                            hasData = true;
                        }
                    });
                    if (hasData) {
                        row[tableHeaders.length - 1] = `$${formatMoney(rowTotal)}`;
                        tableRows.push(row);
                    }
                });
            });

            const totalCols = new Array(tableHeaders.length).fill("");
            totalCols[0] = "TOTALES";
            let grandTotalTable = 0;
            auxiliares.forEach(a => {
                const totalAux = fechas.reduce((sum, f) => {
                    const d = matrix[f]['Día'][a] || {};
                    const n = matrix[f]['Noche'][a] || {};
                    const dSum = (Number(d.turno)||0) + (Number(d.colacion)||0) + (Number(d.movilizacion)||0) + (Number(d.he)||0) + (Number(d.adicional)||0);
                    const nSum = (Number(n.turno)||0) + (Number(n.colacion)||0) + (Number(n.movilizacion)||0) + (Number(n.he)||0) + (Number(n.adicional)||0);
                    return sum + dSum + nSum;
                }, 0);
                totalCols[tableHeaders.indexOf(a)] = `$${formatMoney(totalAux)}`;
                grandTotalTable += totalAux;
            });
            totalCols[tableHeaders.length - 1] = `$${formatMoney(grandTotalTable)}`;

            doc.autoTable({
                head: [tableHeaders],
                body: tableRows,
                startY: 50,
                theme: 'grid',
                styles: { fontSize: 7, cellPadding: 1.5 },
                headStyles: { fillColor: [43, 43, 43], textColor: 255 },
                columnStyles: { 0: { cellWidth: 20 }, 1: { cellWidth: 15 } },
                foot: [totalCols],
                footStyles: { fillColor: [220, 220, 220], textColor: 0, fontStyle: 'bold' },
                showFoot: 'lastPage'
            });

            const saldoAntMail = saldoAnterior;
            let finalYMail = doc.lastAutoTable.finalY + 10;

            if (saldoAntMail > 0) {
                if (finalYMail > doc.internal.pageSize.getHeight() - 30) {
                    doc.addPage();
                    finalYMail = 20;
                }
                doc.setFontSize(10);
                doc.setFont(undefined, 'bold');
                
                doc.setTextColor(0);
                doc.text("Total del Mes:", 110, finalYMail);
                doc.text(`$${formatMoney(totalGeneral)}`, 155, finalYMail);
                
                finalYMail += 5;
                doc.setTextColor(200, 0, 0);
                doc.text("Deuda Anterior:", 110, finalYMail);
                doc.text(`$${formatMoney(saldoAntMail)}`, 155, finalYMail);
                
                finalYMail += 5;
                doc.setTextColor(0, 128, 0);
                doc.text("Total Final:", 110, finalYMail);
                doc.text(`$${formatMoney(totalGeneral + saldoAntMail)}`, 155, finalYMail);
            }

            const pdfBase64 = doc.output('datauristring');

            const adjuntos = [
                {
                    name: `Detalle_Cobranza_${pacienteNombre.replace(/\s+/g, '_')}_${periodo}.pdf`,
                    data: pdfBase64.split(",")[1],
                    mime: 'application/pdf'
                }
            ];

            if (emailAdjuntosSelect && fileBase64) {
                adjuntos.push({
                    name: emailAdjuntosSelect.name,
                    data: fileBase64.split(",")[1],
                    mime: emailAdjuntosSelect.type || 'application/octet-stream'
                });
            }

            await axiosInstance.post("/acuerdo-mutuo/cobranza/notificar", {
                pacienteID,
                periodo,
                numeroNotificacion: notificationType,
                destinatarios: emailDestinatarios,
                asunto: emailAsunto,
                mensaje: emailMessage + `<br/><br/>` + FIRMA_DEFAULT + `<br/><br/><img src="${base64image}" style="max-width:100%; border:1px solid #ddd; border-radius:8px;"/>`,
                adjuntos: adjuntos
            });

            alert(`Notificación ${notificationType} enviada correctamente.`);
            if (onNotificacionEnviada) onNotificacionEnviada(notificationType);
            onClose();
        } catch (error) {
            console.error("Error al enviar correo:", error);
            alert("Ocurrió un error al enviar la notificación.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible) {
            setShowEmailForm(!!modoNotificacion);
            
            // Auto detect notification number (counter)
            const prevNum = parseInt(pacienteData?.numero_notificacion || "0", 10);
            const nextNotif = (prevNum + 1).toString();
            setNotificationType(nextNotif);
            setSelectedTemplateId("");
            
            const avisoPrefix = `Notificación #${nextNotif}`;
            setEmailMessage(`<p>Estimado/a,</p><p>Se adjunta el detalle de cobranza correspondiente al periodo de ${formatearMes(periodo)} (${avisoPrefix}).</p><p>Quedamos atentos a cualquier consulta.</p><p>Saludos cordiales.</p>`);
            
            setEmailMessage(crearMensajeCobranzaDefault(periodo, nextNotif));
            cargarEmailTemplates();

            if (pacienteData) {
                // Combinar correos de notificación y de tutor
                const nats = pacienteData.correos_notificacion ? pacienteData.correos_notificacion.split(',').map(e => e.trim()) : [];
                const tutor = (pacienteData.correo_tutor || pacienteData.tutor_correo || pacienteData.correo || "").split(',').map(e => e.trim());
                
                // Unimos ambos y quitamos posibles vacíos o duplicados
                const todos = [...new Set([...nats, ...tutor])].filter(Boolean).join(", ");
                setEmailDestinatarios(todos);
            }
            setEmailAsunto(`Cobranza SKMC - ${formatearMes(periodo)} - ${pacienteNombre}`);
            setEmailAdjuntosSelect(null);
            setFileBase64(null);
        }
    }, [visible, modoNotificacion, periodo, pacienteData, pacienteNombre, cargarEmailTemplates]);

    // Cuando el detalle carga (async), nos aseguramos de incluir correos de notificación si vienen de la base de datos
    useEffect(() => {
        if (modoNotificacion && data?.paciente?.correos_notificacion) {
            const nats = data.paciente.correos_notificacion.split(',').map(e => e.trim());
            const actuales = emailDestinatarios.split(',').map(e => e.trim());
            const combinados = [...new Set([...actuales, ...nats])].filter(Boolean).join(", ");
            setEmailDestinatarios(combinados);
        }
    }, [data, modoNotificacion]);

    const cargarDetalle = useCallback(async () => {
        try {
            setLoading(true);
            const resp = await axiosInstance.get("/acuerdo-mutuo/cobranza/detalle", {
                params: {
                    fecha: periodo,
                    pacienteID,
                    incluir_snapshot: 1,
                    usar_snapshot: isLiquidado ? 1 : 0,
                }
            });
            setData(normalizarDetalleCobranza(resp.data.data));
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [periodo, pacienteID, isLiquidado]);

    useEffect(() => {
        if (visible && periodo && pacienteID) {
            cargarDetalle();
        } else {
            setData(null);
        }
    }, [visible, periodo, pacienteID, isLiquidado, cargarDetalle]);

    if (!visible) return null;

    // ----- CONSTRUCCIÓN DE LA MATRIZ -----
    let auxiliares = [];
    let fechas = [];
    let matrix = {};

    if (data) {
        // 1. Extraer todos los auxiliares únicos
        const auxiliaresSet = new Set();
        (data.turnos || []).forEach(t => auxiliaresSet.add(t.auxiliar_nombre));
        (data.adicionales || []).forEach(a => auxiliaresSet.add(a.auxiliar_reemplazo_nombre || a.auxiliar_original_nombre || ""));
        auxiliares = Array.from(auxiliaresSet).filter(Boolean).sort();

        // 2. Extraer todas las fechas únicas
        const fechasSet = new Set();
        (data.turnos || []).forEach(t => fechasSet.add(t.fecha));
        (data.adicionales || []).forEach(a => {
            if (a.fecha) fechasSet.add(a.fecha);
            if (a.fechaAdicional) fechasSet.add(a.fechaAdicional);
        });
        fechas = Array.from(fechasSet).filter(Boolean).sort(); 

        // 3. Inicializar matriz
        fechas.forEach(f => {
            matrix[f] = { 'Día': {}, 'Noche': {} };
            auxiliares.forEach(aux => {
                matrix[f]['Día'][aux] = { turno: 0, colacion: 0, movilizacion: 0, he: 0, adicional: 0, motivos: [] };
                matrix[f]['Noche'][aux] = { turno: 0, colacion: 0, movilizacion: 0, he: 0, adicional: 0, motivos: [] };
            });
        });

        // 4. Poblar matriz con turnos
        (data.turnos || []).forEach(t => {
            const f = t.fecha;
            const p = getPeriodoKey(t.periodo);
            const a = t.auxiliar_nombre;
            if (f && matrix[f] && matrix[f][p] && a) {
                matrix[f][p][a].turno += (Number(t.valor_turno_paciente) || 0);
                matrix[f][p][a].colacion += (Number(t.colacion_paciente) || 0);
                matrix[f][p][a].movilizacion += (Number(t.locomocion_paciente) || 0);
                matrix[f][p][a].adicional += (Number(t.otro_paciente) || 0);
            }
        });

        // 5. Poblar matriz con adicionales
        (data.adicionales || []).forEach(ad => {
            const f = ad.fecha || ad.fechaAdicional;
            const p = getPeriodoKey(ad.periodo || ad.tipo_fecha);
            let a = ad.auxiliar_reemplazo_nombre || ad.auxiliar_original_nombre || "";
            
            // Si el nombre viene vacío (ej: auxiliarID 0), intentamos asociarlo al turno que ya exista ese día
            if (!a && f && p) {
                const turnoAsociado = (data.turnos || []).find(t => t.fecha === f && getPeriodoKey(t.periodo) === p);
                if (turnoAsociado) {
                    a = turnoAsociado.auxiliar_nombre;
                } else {
                    // Si no hay turno, buscamos cualquier auxiliar del set para no perder el dato
                    a = auxiliares[0] || "General";
                }
            }

            if (f && matrix[f] && matrix[f][p] && a) {
                if (!matrix[f][p][a]) {
                    matrix[f][p][a] = { turno: 0, colacion: 0, movilizacion: 0, he: 0, adicional: 0, motivos: [], esFS: false };
                }
                
                // Sumamos recargos FS y base de adicional (si tiene) a las líneas base para "pisar" el valor
                matrix[f][p][a].turno += (Number(ad.valor_turno_paciente) || 0) + (Number(ad.recargo_fs_turno) || 0);
                matrix[f][p][a].he += (Number(ad.horas_extras_paciente) || 0);
                matrix[f][p][a].colacion += (Number(ad.colacion_paciente) || 0) + (Number(ad.recargo_fs_colacion) || 0);
                matrix[f][p][a].movilizacion += (Number(ad.locomocion_paciente) || 0) + (Number(ad.recargo_fs_locomocion) || 0);
                
                // Solo dejamos como "adicional" lo que sea verdaderamente un monto extra (Otro)
                matrix[f][p][a].adicional += (Number(ad.otro_paciente) || 0) + (Number(ad.recargo_fs_otro) || 0);
                
                if (ad.fuera_stgo) matrix[f][p][a].esFS = true;
                if (ad.motivo && ad.motivo.trim()) {
                    matrix[f][p][a].motivos.push(ad.motivo.trim());
                }
            }
        });
    }

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", overflowY: "auto" }}>
            <div className="modal-dialog modal-xl modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header d-flex justify-content-between align-items-center">
                        <h5 className="modal-title">
                            Detalle de Cobranza - {pacienteNombre} ({formatearMes(periodo)})
                        </h5>
                        <div>
                            {(isLiquidado || data?.is_snapshot) && (
                                <button
                                    className="btn btn-sm btn-warning mx-2"
                                    onClick={() => setShowEditarModal(true)}
                                    disabled={loading}
                                >
                                    <i className="bi bi-pencil-square me-1"></i>
                                    Editar
                                </button>
                            )}
                            <button 
                                className={`btn btn-sm ${showEmailForm ? 'btn-outline-danger' : 'btn-outline-success'} mx-2`} 
                                onClick={() => setShowEmailForm(!showEmailForm)}
                                disabled={loading}
                            >
                                {showEmailForm ? (
                                    <><i className="bi bi-x-circle me-1"></i> Cerrar Correo</>
                                ) : (
                                    <><i className="bi bi-envelope me-1"></i> Enviar por Correo</>
                                )}
                            </button>
                            <button className="btn-close" style={{marginLeft: 'auto'}} onClick={onClose}></button>
                        </div>
                    </div>

                    {/* FORMULARIO DE CORREO (Se mantiene fuera del 'detalle-cobranza-contenido' para que no salga en la captura) */}
                    {showEmailForm && (
                        <div className="bg-light border-bottom border-primary p-3">
                            <h6 className="text-primary fw-bold mb-3"><i className="bi bi-envelope-paper me-2"></i> Notificar a Paciente</h6>

                            {(data?.tutor?.nombre || pacienteData?.tutor_nombre || data?.paciente?.fecha_inicio_acuerdo || pacienteData?.fecha_inicio_acuerdo) && (
                                <div className="mb-3 d-flex gap-3">
                                    {/* SECCIÓN IZQUIERDA: TUTOR */}
                                    <div className="flex-fill p-2 bg-white border-start border-primary border-4 rounded shadow-sm">
                                        <span className="text-muted small fw-bold text-uppercase d-block mb-1">Tutor:</span>
                                        <span className="text-dark fw-bold fs-6">
                                            {data?.tutor?.nombre || pacienteData?.tutor_nombre || 'Sin tutor'}
                                        </span>
                                        {(data?.tutor?.parentesco || pacienteData?.tutor_parentesco) && (
                                            <span className="badge bg-light text-primary border ms-2">
                                                <i className="bi bi-people me-1"></i>
                                                {data?.tutor?.parentesco || pacienteData?.tutor_parentesco}
                                            </span>
                                        )}
                                    </div>
                                    {/* SECCIÓN DERECHA: FECHA INICIO PACIENTE */}
                                    <div className="flex-fill p-2 bg-white border-start border-success border-4 rounded shadow-sm">
                                        <span className="text-muted small fw-bold text-uppercase d-block mb-1">Fecha Inicio Paciente:</span>
                                        <span className="text-dark fw-bold fs-6">
                                            {(data?.paciente?.fecha_inicio_acuerdo || pacienteData?.fecha_inicio_acuerdo) ? ((data?.paciente?.fecha_inicio_acuerdo || pacienteData?.fecha_inicio_acuerdo).split(" ")[0].split("-").reverse().join("-")) : '-'}
                                        </span>
                                    </div>
                                </div>
                            )}
                            <div className="row g-3">
                                <div className="col-md-12">
                                    <label className="form-label fw-bold text-dark small">Destinatarios (separados por coma)</label>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        value={emailDestinatarios}
                                        onChange={(e) => setEmailDestinatarios(e.target.value)}
                                        disabled={loading}
                                        placeholder="correo@ejemplo.com, otro@ejemplo.com"
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold text-dark small">Número de Notificación (Autoincremental)</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-primary text-white fw-bold">#</span>
                                        <input 
                                            type="text" 
                                            className="form-control fw-bold bg-white text-center" 
                                            value={notificationType} 
                                            readOnly 
                                            disabled
                                        />
                                    </div>
                                    <div className="mt-3 text-muted small">
                                        <i className="bi bi-paperclip me-1"></i>
                                        El detalle de abajo se adjuntará como imagen al cuerpo del correo y como PDF adjunto.
                                    </div>
                                    <div className="mt-4">
                                        <label className="form-label fw-bold text-dark small">Adjuntar archivo extra (Opcional)</label>
                                        <input 
                                            type="file" 
                                            className="form-control form-control-sm"
                                            onChange={(e) => {
                                                if (e.target.files.length > 0) {
                                                    const f = e.target.files[0];
                                                    setEmailAdjuntosSelect(f);
                                                    const reader = new FileReader();
                                                    reader.onload = () => {
                                                        setFileBase64(reader.result);
                                                    };
                                                    reader.readAsDataURL(f);
                                                } else {
                                                    setEmailAdjuntosSelect(null);
                                                    setFileBase64(null);
                                                }
                                            }}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-8">
                                    <label className="form-label fw-bold text-dark small">Asunto</label>
                                    <input
                                        type="text"
                                        className="form-control mb-3"
                                        value={emailAsunto}
                                        onChange={(e) => setEmailAsunto(e.target.value)}
                                        disabled={loading}
                                    />
                                    <label className="form-label fw-bold text-dark small">Template de Correo</label>
                                    <select
                                        className="form-select mb-3"
                                        value={selectedTemplateId}
                                        onChange={(e) => handleSeleccionTemplate(e.target.value)}
                                        disabled={loading}
                                    >
                                        <option value="">Mensaje por defecto</option>
                                        {emailTemplates.map((template) => (
                                            <option key={template.id} value={template.id}>
                                                {template.nombre}
                                            </option>
                                        ))}
                                    </select>
                                    <label className="form-label fw-bold text-dark small">Mensaje al Paciente</label>
                                    <div style={{ backgroundColor: '#fff', borderRadius: '4px' }}>
                                        <ReactQuill 
                                            theme="snow"
                                            value={emailMessage}
                                            onChange={setEmailMessage}
                                            readOnly={loading}
                                            modules={{
                                                toolbar: [
                                                    ['bold', 'italic', 'underline', 'strike'],
                                                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                                    [{ 'color': [] }, { 'background': [] }],
                                                ]
                                            }}
                                            style={{ height: '150px', marginBottom: '50px' }}
                                        />
                                    </div>
                                    <div className="mt-2 p-3 bg-white border rounded shadow-sm" style={{ opacity: 0.85 }}>
                                        <div className="small text-muted mb-2"><i className="bi bi-info-circle me-1"></i> Firma Automática (No editable):</div>
                                        <div dangerouslySetInnerHTML={{ __html: FIRMA_DEFAULT }} />
                                    </div>
                                </div>
                            </div>
                            <div className="mt-4 text-end">
                                <button 
                                    className="btn btn-primary px-4 fw-bold shadow-sm" 
                                    onClick={handleEnviarCorreo}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <><span className="spinner-border spinner-border-sm me-2"></span>Enviando Correo...</>
                                    ) : (
                                        <><i className="bi bi-send me-2"></i>Enviar Notificación</>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* INDICADORES ARRIBA (No se incluyen en la captura del PDF/Imagen del cuerpo) */}
                    {!loading && data && (
                        <div className="px-4 pt-3 pb-2 bg-light border-bottom border-secondary">
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <div className="p-2 bg-white border-start border-danger border-4 rounded shadow-sm text-center border overflow-hidden h-100">
                                        <span className="text-muted small fw-bold text-uppercase d-block mb-1">Saldo Anterior</span>
                                        <span className="text-danger fw-bold fs-5">${formatMoney(data?.saldo_anterior || 0)}</span>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="p-2 bg-white border-start border-primary border-4 rounded shadow-sm text-center border overflow-hidden h-100">
                                        <span className="text-muted small fw-bold text-uppercase d-block mb-1">Consolidado (Anterior + Mes)</span>
                                        <span className={`fw-bold fs-5 ${consolidado > 0 ? 'text-danger' : 'text-primary'}`}>
                                            ${formatMoney(consolidado)}
                                        </span>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="p-2 bg-white border-start border-success border-4 rounded shadow-sm text-center border overflow-hidden h-100">
                                        <span className="text-muted small fw-bold text-uppercase d-block mb-1">TOTAL DEL MES</span>
                                        <span className="text-success fw-bold fs-5">${formatMoney(totalGeneralCalculado)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="modal-body" id="detalle-cobranza-contenido">
                        {loading && (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary"></div>
                                <p className="mt-3">Cargando detalle...</p>
                            </div>
                        )}

                        {!loading && data && (
                            <>
                                {data.is_snapshot && (
                                    <div className="alert alert-info py-2" style={{ fontSize: "0.9rem" }}>
                                        <i className="bi bi-info-circle me-2"></i>
                                        Este período de cobranza está liquidado. Los turnos y adicionales mostrados corresponden al detalle histórico congelado.
                                    </div>
                                )}

                                <div className="text-center mb-4">
                                    <h4 className="fw-bold text-dark mb-1">{pacienteNombre}</h4>
                                    <p className="text-muted small text-uppercase fw-bold m-0">{formatearMes(periodo)}</p>
                                </div>

                                <div className="p-4 mb-4 border shadow-sm" style={{ background: "#f8f9fa", borderRadius: "12px", borderLeft: "5px solid #0d6efd" }}>
                                    {/* BLOQUE DE CONCEPTOS BASE */}
                                    <div className="row g-3 text-center align-items-stretch mb-3"> {/* Changed align-items-center to align-items-stretch */}
                                        <div className="col-md-3 d-flex"> {/* Added d-flex */}
                                            <div className="p-2 bg-white rounded shadow-sm flex-fill"> {/* Added flex-fill */}
                                                <div className="text-muted small text-uppercase fw-bold mb-1" style={{fontSize: '0.65rem'}}>Total Turnos (Base + FS)</div>
                                                <div className="fs-6 text-dark fw-bold">
                                                    ${formatMoney(resumenDetalle.totalTurnosBaseFS)}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3 d-flex"> {/* Added d-flex */}
                                            <div className="p-2 bg-white rounded shadow-sm flex-fill"> {/* Added flex-fill */}
                                                <div className="text-muted small text-uppercase fw-bold mb-1" style={{fontSize: '0.65rem'}}>Beneficios (Alim. + Mov.)</div>
                                                <div className="fs-6 text-dark fw-bold">
                                                    ${formatMoney(resumenDetalle.totalBeneficios)}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3 d-flex"> {/* Added d-flex */}
                                            <div className="p-2 bg-white rounded shadow-sm flex-fill"> {/* Added flex-fill */}
                                                <div className="text-muted small text-uppercase fw-bold mb-1" style={{fontSize: '0.65rem'}}>Otros / Horas Extras</div>
                                                <div className="fs-6 text-dark fw-bold">
                                                    ${formatMoney(resumenDetalle.totalOtrosHE)}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3 d-flex"> {/* Moved this div and added d-flex */}
                                            <div className="p-2 bg-success text-white rounded shadow-sm flex-fill"> {/* Added flex-fill */}
                                                <div className="small text-uppercase fw-bold mb-1" style={{fontSize: '0.65rem'}}>Total del Periodo</div>
                                                <div className="fs-5 fw-bold">${formatMoney(totalGeneralCalculado)}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* SECCIÓN DE AJUSTES (SOLO SI EXISTEN) */}
                                    {(data.otros_suma > 0 || data.otros_resta > 0) && (
                                        <div className="mt-4 p-3 bg-white border rounded shadow-sm mb-3">
                                            <div className="text-uppercase fw-bold text-muted mb-2 border-bottom pb-1" style={{fontSize: '0.75rem'}}>
                                                <i className="bi bi-gear-fill me-2 text-secondary"></i>Ajustes del Periodo
                                            </div>
                                            <div className="d-flex justify-content-around align-items-center">
                                                <div className="text-center">
                                                    <small className="text-muted d-block">Subtotal Periodo</small>
                                                    <span className="fw-bold">${formatMoney(totalGeneralCalculado - (data.otros_suma || 0) + (data.otros_resta || 0))}</span>
                                                </div>
                                                <i className="bi bi-chevron-right text-muted"></i>
                                                {data.otros_suma > 0 && (
                                                    <div className="text-center">
                                                        <small className="text-success d-block lh-1">Otros Cargos (+)</small>
                                                        <span className="fw-bold text-success">+${formatMoney(data.otros_suma)}</span>
                                                    </div>
                                                )}
                                                {data.otros_resta > 0 && (
                                                    <div className="text-center">
                                                        <small className="text-danger d-block lh-1">Otros Descuentos (-)</small>
                                                        <span className="fw-bold text-danger">-${formatMoney(data.otros_resta)}</span>
                                                    </div>
                                                )}
                                                <i className="bi bi-arrow-right-circle-fill text-success fs-5"></i>
                                                <div className="text-center">
                                                    <small className="text-muted d-block lh-1">TOTAL FINAL</small>
                                                    <span className="fw-bold text-success fs-5">${formatMoney(totalGeneralCalculado)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* SECCIÓN DE OBSERVACIÓN (SI EXISTE) */}
                                    {data.observacion && (
                                        <div className="mt-3 p-3 text-dark rounded border shadow-sm" style={{ background: "#fff9db", borderLeft: "5px solid #fcc419 !important" }}>
                                            <div className="fw-bold text-dark mb-1" style={{fontSize: '0.85rem'}}>
                                                <i className="bi bi-chat-left-dots-fill me-2 text-warning"></i>Observaciones y Notas del Cobro:
                                            </div>
                                            <div style={{ fontSize: "0.9rem", whiteSpace: "pre-wrap" }}>
                                                {data.observacion}
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="text-center mt-3 pt-2">
                                        <small className="text-muted opacity-75">La siguiente tabla desglosa cada servicio prestado durante el periodo:</small>
                                    </div>
                                </div>

                                {fechas.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-striped mt-3" style={{ fontSize: "0.9rem" }}>
                                            <thead className="table-light align-middle text-center">
                                                <tr>
                                                    <th>Fecha</th>
                                                    <th>Turno</th>
                                                    {auxiliares.map(a => (
                                                        <th key={a} style={{ minWidth: "120px" }}>{a}</th>
                                                    ))}
                                                    <th className="bg-secondary text-white">Total Día</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    const groupFechas = [];
                                                    let currentGroupLabel = "";
                                                    const [perY, perM] = periodo.split('-').map(Number);
                                                    
                                                    fechas.forEach(f => {
                                                        const [yStr, mStr, dStr] = f.split('-');
                                                        let y = parseInt(yStr, 10);
                                                        let m = parseInt(mStr, 10);
                                                        const d = parseInt(dStr, 10);

                                                        let billingM = m;
                                                        let billingY = y;

                                                        if (d >= 26) {
                                                            billingM += 1;
                                                            if (billingM > 12) {
                                                                billingM = 1;
                                                                billingY += 1;
                                                            }
                                                        }

                                                        const isAdelanto = billingY > perY || (billingY === perY && billingM > perM);
                                                        const meses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
                                                        
                                                        const label = `${meses[billingM - 1]} - ${billingY} ${isAdelanto ? '(Adelanto)' : ''}`.trim();

                                                        if (label !== currentGroupLabel) {
                                                            groupFechas.push({ type: 'header', label: label });
                                                            currentGroupLabel = label;
                                                        }
                                                        groupFechas.push({ type: 'date', f: f });
                                                    });

                                                    return groupFechas.map((item, index) => {
                                                        if (item.type === 'header') {
                                                            return (
                                                                <tr key={`header-${index}`} className="table-secondary">
                                                                    <td colSpan={3 + auxiliares.length} className="fw-bold text-center text-uppercase fs-6">
                                                                        {item.label}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }

                                                        const f = item.f;
                                                    const rowDia = matrix[f]['Día'];
                                                    const rowNoche = matrix[f]['Noche'];
                                                    
                                                    const totalDia = auxiliares.reduce((sum, a) => sum + rowDia[a].turno + rowDia[a].he + rowDia[a].colacion + rowDia[a].movilizacion + rowDia[a].adicional, 0);
                                                    const totalNoche = auxiliares.reduce((sum, a) => sum + rowNoche[a].turno + rowNoche[a].he + rowNoche[a].colacion + rowNoche[a].movilizacion + rowNoche[a].adicional, 0);

                                                    // Para no mostrar días completamente vacíos si por alguna razón no tienen montos
                                                    if (totalDia === 0 && totalNoche === 0) return null;

                                                    return (
                                                        <React.Fragment key={f}>
                                                            {/* FILA DÍA */}
                                                            <tr>
                                                                <td rowSpan={2} className="align-middle fw-bold">{formatearFecha(f)}</td>
                                                                <td className="text-danger fw-bold text-center align-middle">Día</td>
                                                                {auxiliares.map(a => {
                                                                    const cell = rowDia[a];
                                                                    const cellTotal = cell.turno + cell.colacion + cell.movilizacion + cell.he + cell.adicional;
                                                                    return (
                                                                        <td key={a} className="text-end" style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                                                                            {cellTotal > 0 ? (
                                                                                <>
                                                                                    {cell.turno > 0 && <div>Turno {cell.esFS && <span className="badge bg-warning text-dark p-1" style={{fontSize: '0.65rem'}}>FS</span>}: ${formatMoney(cell.turno)}</div>}
                                                                                    {cell.colacion > 0 && <div>Alim.: ${formatMoney(cell.colacion)}</div>}
                                                                                    {cell.movilizacion > 0 && <div>Mov.: ${formatMoney(cell.movilizacion)}</div>}
                                                                                    {cell.he > 0 && <div>HE: ${formatMoney(cell.he)}</div>}
                                                                                     {cell.adicional > 0 && (
                                                                                        <div>
                                                                                            Adic.: ${formatMoney(cell.adicional)}
                                                                                            {cell.motivos && cell.motivos.length > 0 && (
                                                                                                <div className="text-muted" style={{ fontSize: "0.7rem", fontStyle: "italic" }}>
                                                                                                    ({cell.motivos.join(", ")})
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    )}
                                                                                     {/* Total interno removido por solicitud del usuario */}
                                                                                 </>
                                                                             ) : '-'}
                                                                         </td>
                                                                     );
                                                                 })}
                                                                 <td className="text-end fw-bold bg-light align-middle">
                                                                     {totalDia > 0 ? `$${formatMoney(totalDia)}` : '-'}
                                                                 </td>
                                                             </tr>
                                                             {/* FILA NOCHE */}
                                                             <tr>
                                                                 <td className="text-primary fw-bold text-center align-middle">Noche</td>
                                                                 {auxiliares.map(a => {
                                                                     const cell = rowNoche[a];
                                                                     const cellTotal = cell.turno + cell.colacion + cell.movilizacion + cell.he + cell.adicional;
                                                                     return (
                                                                         <td key={a} className="text-end" style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                                                                             {cellTotal > 0 ? (
                                                                                 <>
                                                                                     {cell.turno > 0 && <div>Turno {cell.esFS && <span className="badge bg-warning text-dark p-1" style={{fontSize: '0.65rem'}}>FS</span>}: ${formatMoney(cell.turno)}</div>}
                                                                                     {cell.colacion > 0 && <div>Alim.: ${formatMoney(cell.colacion)}</div>}
                                                                                     {cell.movilizacion > 0 && <div>Mov.: ${formatMoney(cell.movilizacion)}</div>}
                                                                                     {cell.he > 0 && <div>HE: ${formatMoney(cell.he)}</div>}
                                                                                    {cell.adicional > 0 && (
                                                                                        <div>
                                                                                            Adic. {cell.esFS && <span className="badge bg-warning text-dark p-1" style={{fontSize: '0.65rem'}}>FS</span>}: ${formatMoney(cell.adicional)}
                                                                                            {cell.motivos.length > 0 && (
                                                                                                <div className="text-muted" style={{ fontSize: "0.7rem", fontStyle: "italic" }}>
                                                                                                    ({cell.motivos.join(", ")})
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    )}
                                                                                     {/* Total interno removido por solicitud del usuario */}
                                                                                 </>
                                                                             ) : '-'}
                                                                        </td>
                                                                    );
                                                                })}
                                                                <td className="text-end fw-bold bg-light align-middle">
                                                                    {totalNoche > 0 ? `$${formatMoney(totalNoche)}` : '-'}
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    );
                                                    });
                                                })()}

                                                {/* FILA DE TOTALES POR AUXILIAR */}
                                                <tr className="table-dark align-middle">
                                                    <td colSpan={2} className="text-end fw-bold">TOTALES TURNOS:</td>
                                                    {auxiliares.map(a => {
                                                        const totalAux = fechas.reduce((sum, f) => {
                                                            const { turno: d1, colacion: d2, movilizacion: d3, he: dH, adicional: d4 } = matrix[f]['Día'][a];
                                                            const { turno: n1, colacion: n2, movilizacion: n3, he: nH, adicional: n4 } = matrix[f]['Noche'][a];
                                                            return sum + d1 + d2 + d3 + dH + d4 + n1 + n2 + n3 + nH + n4;
                                                        }, 0);
                                                        return (
                                                            <td key={a} className="text-end fw-bold">
                                                                ${formatMoney(totalAux)}
                                                            </td>
                                                        );
                                                    })}
                                                    <td className="text-end fw-bold fs-6">
                                                        ${formatMoney(
                                                            fechas.reduce((sum, f) => 
                                                                sum + auxiliares.reduce((s, a) => {
                                                                    const { turno: d1, colacion: d2, movilizacion: d3, he: dH, adicional: d4 } = matrix[f]['Día'][a];
                                                                    const { turno: n1, colacion: n2, movilizacion: n3, he: nH, adicional: n4 } = matrix[f]['Noche'][a];
                                                                    return s + d1 + d2 + d3 + dH + d4 + n1 + n2 + n3 + nH + n4;
                                                                }, 0)
                                                            , 0)
                                                        )}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="alert alert-info text-center mt-3">No hay turnos registrados en este periodo.</div>
                                )}

                                {Number(data?.saldo_anterior) > 0 && (
                                    <div className="d-flex justify-content-end mt-4">
                                        <div className="p-3 border rounded shadow-sm bg-white border-2 border-primary" style={{ minWidth: '350px' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                                                <span className="text-muted fw-bold fs-6">Total del Mes:</span>
                                                <span className="text-success fw-bold fs-6">${formatMoney(totalGeneralCalculado)}</span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                                                <span className="text-muted fw-bold fs-6">Deuda Anterior:</span>
                                                <span className="text-danger fw-bold fs-6">${formatMoney(data?.saldo_anterior || 0)}</span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center pt-1">
                                                <span className="text-dark fw-bolder fs-5">Total Final:</span>
                                                <span className="text-dark fw-bolder fs-5">${formatMoney(consolidado)}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
                            Cerrar
                        </button>
                        <button className="btn btn-danger text-white" onClick={handleDescargarPDF} disabled={loading}>
                            <i className="bi bi-file-earmark-pdf me-2"></i>PDF (Foto)
                        </button>
                        <button className="btn btn-warning text-dark" onClick={handleDescargarPDF_HTML} disabled={loading}>
                            <i className="bi bi-lightning-fill me-2"></i>PDF Ligero (HTML)
                        </button>
                        <button className="btn btn-info text-white" onClick={handleCopiarImagen} disabled={loading}>
                            <i className="bi bi-clipboard-check me-2"></i>Copiar Imágen
                        </button>
                    </div>
                </div>
            </div>
            <EditarCobranzaModal
                visible={showEditarModal}
                onClose={() => setShowEditarModal(false)}
                detalleInicial={data || {}}
                pacienteID={pacienteID}
                pacienteNombre={pacienteNombre}
                periodo={periodo}
                onSaved={cargarDetalle}
            />
        </div>
    );
};

export default CobranzaDetalle;
